import { BomTreeNodeType, INodeWithBomChildren } from '../bom'
import { IDocumentDTO } from '../document'
import { IMaterialPriceData } from './IMaterialPriceData'

export class MaterialListItem implements IMaterialPriceData, INodeWithBomChildren {

    constructor(source?: Partial<MaterialListItem>) {
        if (source) {
            Object.assign(this, source)
        }
    }

    id: number = 0
    nodeType?: BomTreeNodeType
    description: string = ""
    statusText?: string
    expandIconClass: string = ""

    materialNo?: number
    salesText?: string
    documents?: IDocumentDTO[]

    availableStock?: number

    sparePartPackage?: string

    hasProductionBom?: boolean
    hasSeveralAlternativeBoms?: boolean
    hasMaintenanceBom?: boolean
    hasBoughtWithBom?: boolean
    hasAlternativeItemsBom?: boolean
    hasReplacement?: boolean

    hasRecursiveProductionBom?: boolean
    hasRecursiveMaintenanceBom?: boolean
    hasRecursiveBoughtWithBom?: boolean
    hasRecursiveAlternativeItemsBom?: boolean

    hasPicture?: boolean

    isBulk?: boolean

    qty?: number
    unit?: string

    parentItem?: MaterialListItem
    childItems?: MaterialListItem[] // actual child items
    visibleChildItems?: MaterialListItem[] // child items to display in list

    leafNodeCount: number = 0

    sparePartStatus?: string
    price?: number
    priceQty?: number
    priceQtyUnit?: string
    isSparePart: boolean = false
    hiddenSparePart: boolean = false
    sparePartRequiresSerialNumber: boolean = false

    partnerMaterialNo?: string

    get isLeaf(): boolean {
        return !this.childItems?.length
    }

    get hasSpecialChildNode(): boolean {
        return Boolean(this?.hasBoughtWithBom ||
            this?.hasMaintenanceBom ||
            this?.hasProductionBom ||
            this?.hasAlternativeItemsBom ||
            this?.hasReplacement ||
            this?.hasSeveralAlternativeBoms)
    }

    get isSpecialNode(): boolean {
        return this.expandIconClass != ''
    }

    get isCategoryNode(): boolean {
        return !this.materialNo && !this.expandIconClass
    }

    get isChildOfCategoryNode(): boolean {
        return this.parentItem != null && this.parentItem.isCategoryNode
    }

    get topMostParentMaterialOf(): MaterialListItem | null {
        if (this.parentItem == null) return null
        if (this.materialNo && this.isChildOfCategoryNode) return this
        return this.parentItem.topMostParentMaterialOf
    }

    get productionItems(): MaterialListItem[] | undefined {
        return this.childItems?.find(item => item.nodeType == BomTreeNodeType.ProductionNode)
            ?.childItems
            ?.sortBy(i => i.materialNo)
    }

    get recommendedItems(): MaterialListItem[] | undefined {
        return this.childItems?.find(item => item.nodeType == BomTreeNodeType.RecommendedItemsNode)
            ?.childItems
            ?.sortBy(i => i.materialNo)
    }

    get maintenanceItems(): MaterialListItem[] | undefined {
        return this.childItems
            ?.find(item => item.nodeType == BomTreeNodeType.MaintenanceNode)
            ?.childItems
            ?.sortBy(i => i.materialNo)
    }

    get alternativeItems(): MaterialListItem[] | undefined {
        return this.childItems
            ?.find(item => item.nodeType == BomTreeNodeType.AlternativeItemsNode)
            ?.childItems
            ?.sortBy(i => i.materialNo)
    }

    get replacementItems(): MaterialListItem[] | undefined {
        return this.childItems
            ?.find(item => item.nodeType == BomTreeNodeType.ReplacementNode)
            ?.childItems
            ?.sortBy(i => i.materialNo)
    }

    get relatedItems(): MaterialListItem[] | undefined {
        const productionItems = allItemsInclAlternativeBomItems(this.productionItems ?? [])
            .filter(item => item.isSparePart)

        const maintenanceItems = allItemsInclAlternativeBomItems(this.maintenanceItems ?? [])
            .filter(item => item.isSparePart)

        // Merge, removing any duplicates (preferring qty from production items)
        const result = [...productionItems, ...maintenanceItems]
            .reduce((acc: MaterialListItem[], item: MaterialListItem) => {
                if (acc.every(x => x.materialNo != item.materialNo)) {
                    acc.push(item)
                }
                return acc
            }, [])

        return result?.length
            ? result.sortBy(i => i.materialNo)
            : undefined
    }
}

function allItemsInclAlternativeBomItems(items: MaterialListItem[]): MaterialListItem[] {
    return items
        .filter(item => item.nodeType != BomTreeNodeType.AlternativeBomNode)
        .concat(
            items
                .filter(item => item.nodeType == BomTreeNodeType.AlternativeBomNode)
                .flatMap(item => item.childItems ?? [])
        )
}